import type { Document } from '@contentful/rich-text-types';

import { FaqAccordion, withErrorBoundary } from '../../components';
import { documentToReactComponents } from '../../components/RichText/helpers';
import { StyledModuleContainer } from '../../shared';

import { sortFaqItemsByOrder } from './helpers';

export interface FaqModuleItem {
  /** Resolved Contentful rich text document (raw already parsed by the transform). */
  answer: Document | null;
  /** Render this item open on first paint. */
  expandedByDefault?: boolean;
  /** Stable identifier (Contentful entry id). */
  id: string;
  /**
   * Manual sort order (ascending). Absent until Contentful migration 0197 is
   * applied; items without one keep their array position after ordered items.
   */
  order?: number | null;
  question: string;
}

export interface FaqModuleProps {
  items: FaqModuleItem[];
  showLessLabel?: string;
  showMoreLabel?: string;
  title: string;
}

/**
 * Page-section wrapper around FaqAccordion: maps Contentful FAQ data (already
 * transformed by the app) to accordion props and renders rich-text answers.
 * Per BRLYAH-15 spec it deliberately ignores faqCategory's legacy `image`,
 * `hero` and `subcategories` fields.
 */
const FaqModule = ({ items, showLessLabel, showMoreLabel, title }: FaqModuleProps) => {
  const accordionItems = sortFaqItemsByOrder(items).map(({ answer, expandedByDefault, id, question }) => ({
    answer: answer ? documentToReactComponents(answer) : null,
    expandedByDefault,
    id,
    question,
  }));

  return (
    <StyledModuleContainer>
      <FaqAccordion items={accordionItems} showLessLabel={showLessLabel} showMoreLabel={showMoreLabel} title={title} />
    </StyledModuleContainer>
  );
};

export default withErrorBoundary(FaqModule, { componentName: 'FaqModule' });
